

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate as useRouter, useLocation } from "react-router-dom";
import { SearchItem, SearchEvent } from "./useSearch";

export const useHandleOps = (
  searchResults: SearchItem[],
  onClose: () => void,
  lockedEvent: SearchEvent | null,
  lockEvent: (eventId: string) => void,
  unlockEvent: () => void,
  searchState: string,
  setSearchState: (value: string) => void,
  ghostText: string,
  atGhostText: string,
) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const router = useRouter();
  const pathname = useLocation().pathname;

  // Focus input immediately + delayed fallback for portal mount timing
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Re-focus input after locking/unlocking an event
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
    return () => clearTimeout(timer);
  }, [lockedEvent]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  const navigateToItem = useCallback(
    (item: SearchItem) => {
      onClose();

      // Action rows (e.g. open AI assistant) run a callback instead of routing.
      if (item.action) {
        item.action();
        return;
      }

      // Already on this page — no navigation will happen, so don't start the
      // spinner (navigationEnd would never fire and it would spin forever)
      if (item.url === pathname) return;

      // Dispatch to GlobalSearch so spinner persists after PSearch unmounts
      window.dispatchEvent(new Event("searchNavigationStart"));

      // Dispatch navigationStart for sidebar/other listeners
      window.dispatchEvent(
        new CustomEvent("navigationStart", { detail: { url: item.url } }),
      );

      router(item.url);
    },
    [onClose, router, pathname],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        e.preventDefault();

        const item = searchResults[selectedIndex];

        // Priority 1: lock into event (existing behaviour)
        if (item?.eventId && !lockedEvent) {
          lockEvent(item.eventId);
          return;
        }

        // Priority 2: complete the @event name being typed
        if (atGhostText) {
          setSearchState(searchState + atGhostText);
          return;
        }

        // Priority 3: autocomplete page/setting label
        if (ghostText) {
          setSearchState(searchState + ghostText);
          return;
        }

        return;
      }

      // Backspace on empty input: unlock event
      if (e.key === "Backspace" && lockedEvent && searchState === "") {
        e.preventDefault();
        unlockEvent();
        return;
      }

      if (searchResults.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % searchResults.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + searchResults.length) % searchResults.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = searchResults[selectedIndex];
        if (item) {
          if (item.eventId && !lockedEvent) {
            lockEvent(item.eventId);
          } else {
            navigateToItem(item);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    selectedIndex,
    searchResults,
    navigateToItem,
    lockedEvent,
    lockEvent,
    unlockEvent,
    searchState,
    setSearchState,
    ghostText,
    atGhostText,
  ]);

  return {
    inputRef,
    selectedIndex,
    navigateToItem,
  };
};
